import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import Card from '../../../components/Card';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { translate } from '../../../services/translate';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { dispatchService } from '../../../services/central-api/dispatch';
import { useQuery } from '@tanstack/react-query';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { TextInputMask } from 'react-native-masked-text';
import { makeSetStateHandler } from '../../../components/helpers/form';

interface Props {
    isModalVisible: boolean;
    onClose: () => void;
    patrolActivityId?: number;
}

interface PatrolActivityForm {
    name: string;
    code: string;
    needAttachment: boolean;
    durationInMinutes: number;
    actingBodyId: number;
}

export default function PatrolActivityModal({ isModalVisible, onClose, patrolActivityId }: Props) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [isSendingData, setIsSendingData] = useState<boolean>(false);

    const defaultEmptyValues: PatrolActivityForm = {
        actingBodyId: 0,
        code: '',
        name: '',
        needAttachment: false,
        durationInMinutes: 0,
    };
    const form = useForm<PatrolActivityForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const queryResult = useQuery({
        queryKey: ['getPatrolActivityById', patrolActivityId, isModalVisible],
        queryFn: () => dispatchService.getPatrolActivityById(patrolActivityId!),
        enabled: !!authenticatedUser && !!patrolActivityId && isModalVisible,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data } = queryResult;

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    useEffect(() => {
        if (isModalVisible && authenticatedUser && !authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_patrol_activities) {
            form.setValue('actingBodyId', authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }, [isModalVisible, authenticatedUser]);

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        form.reset({
            actingBodyId: data.actingBodyId,
            code: data.code,
            name: data.name,
            needAttachment: data.needAttachment,
            durationInMinutes: data.durationInMinutes
        });

    }, [data, authenticatedUser]);

    async function formSubmit() {
        setIsSendingData(true);
        try {
            const values = form.getValues();
            if (patrolActivityId) {
                await dispatchService.updatePatrolActivity({
                    id: patrolActivityId,
                    name: values.name,
                    code: values.code,
                    needAttachment: values.needAttachment,
                    durationInMinutes: values.durationInMinutes
                });
            } else {
                await dispatchService.createPatrolActivity({
                    name: values.name,
                    code: values.code,
                    actingBodyId: values.actingBodyId,
                    needAttachment: values.needAttachment,
                    durationInMinutes: values.durationInMinutes
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
            onClose();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsSendingData(false);
        }
    }
    const values = useWatch({ control: form.control });
    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                clearValues();
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 450 }]}>
                    <Card style={{ padding: 0, gap: 8 }}>
                        <View style={{ flexDirection: 'column', rowGap: 8, paddingBottom: 24 }}>
                            <MyAppText style={styles.header}>{translate('registerActivity')}</MyAppText>
                            <MyAppText style={styles.subheader}>{translate('fillTheFieldsToRegister')}</MyAppText>
                        </View>
                        <ActingBodiesDropdown
                            form={form}
                            fieldName='actingBodyId'
                            zIndex={2}
                            labelStyle={styles.fieldsLabelStyle}
                            viewStyle={{ gap: 8 }}
                            required
                            disabled={!!patrolActivityId || !authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_patrol_activities}
                        />
                        <View style={{ flexDirection: 'row', gap: 12 }}>
                            <View style={{ flex: 1 }}>
                                <FormInput
                                    form={form}
                                    fieldName='code'
                                    required
                                    label={translate('code')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                            </View>
                            <View style={{ flex: 2 }}>
                                <FormInput
                                    form={form}
                                    fieldName='name'
                                    required
                                    label={translate('name')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                            </View>
                        </View>

                        <View style={{ gap: 8 }}>
                            <MyAppText style={styles.fieldsLabelStyle}>
                                {translate('activityDuration')}
                            </MyAppText>
                            <View style={[{ flexDirection: 'row' }]}>
                                <TextInputMask
                                    value={String(values.durationInMinutes)}
                                    includeRawValueInChangeText={true}
                                    onChangeText={(maskedText, rawText) => {
                                        const setFunction = makeSetStateHandler(form, 'durationInMinutes');
                                        setFunction(Number(rawText));
                                    }}
                                    type={'only-numbers'}
                                    style={[styles.inputAttached, { maxWidth: 70, textAlign: 'center' }]}
                                />
                                <MyAppText style={styles.inputAttach}>{translate('minutes')}</MyAppText>
                            </View>
                        </View>
                        <MyCheckbox
                            form={form}
                            fieldName='needAttachment'
                            label={translate('needAttachment')}
                        />
                        <FormActions
                            viewStyle={{ flex: 1, justifyContent: 'space-around', paddingTop: 24 }}
                            buttonStyle={{ paddingHorizontal: 9, paddingVertical: 9 }}
                            buttonContentContainerStyle={{ flex: 1, }}
                            textStyle={{ fontSize: 16, fontWeight: '600' }}
                            onSubmit={formSubmit}
                            isLoading={isLoading || isSendingData}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!form.formState.isValid} />
                    </Card>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputAttached: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.container.background,
        maxHeight: 40,
        padding: 10,
        color: theme.colors.textColor,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopStartRadius: 4,
        borderBottomStartRadius: 4,
    },
    inputAttach: {
        color: theme.colors.textColor,
        fontSize: 13,
        backgroundColor: theme.colors.container.border,
        maxHeight: 40,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: theme.colors.container.border,
        borderTopEndRadius: 4,
        borderBottomEndRadius: 4,
        padding: 11,
    },
}));
